// src/components/NotificationsSettings.js
// Modèles de notifications — organized by event type, the axis the backend
// resolves templates on at send time (notifications_service: active-only,
// brand + language → brand + EN → user-level + language → user-level + EN).
// One card per event type with template rows (lang chip · scope · activation
// switch · subject); one unified create/edit modal with a live Code/Aperçu
// HTML preview. Filters (q, lang, brand) live in the URL.
// API: /api/notifications/html-content (CRUD), /notification-types.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './NotificationsSettings.css';
import { useTranslation } from 'react-i18next';
import {
    FaBell, FaPlus, FaPen, FaTrash, FaSpinner, FaSearch, FaTimes,
    FaCheckCircle, FaTimesCircle, FaEye, FaExclamationTriangle
} from 'react-icons/fa';

const DEFAULT_LANGUAGE = 'EN';

const normalizeLanguageValue = (language) => (language ? language.trim().toUpperCase() : DEFAULT_LANGUAGE);

// The five backend event types (NotificationType enum), in display order.
const TYPE_ORDER = ['ticket_created', 'ticket_created_by_mail', 'response_received', 'outlook_reply', 'client_email'];

const emptyForm = () => ({
    name: '',
    notification_type: '',
    subject: '',
    html_content: '',
    description: '',
    is_active: true,
    language: DEFAULT_LANGUAGE,
    brand_id: ''
});

const NotificationsSettings = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    const [user, setUser] = useState(null);
    const [notifications, setNotifications] = useState(null); // null = loading
    const [notificationTypes, setNotificationTypes] = useState([]);
    const [brands, setBrands] = useState([]);
    const [feedback, setFeedback] = useState(null);
    const feedbackTimer = useRef(null);

    const searchTerm = searchParams.get('q') || '';
    const languageFilter = searchParams.get('lang') || '';
    const brandFilter = searchParams.get('brand') || '';

    const updateParams = (updates) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === undefined || value === '') next.delete(key);
            else next.set(key, String(value));
        });
        setSearchParams(next, { replace: true });
    };

    // modal = {editing, form, error, saving, view: 'code'|'preview'}
    const [modal, setModal] = useState(null);
    // deleteModal = {notification, deleting, error}
    const [deleteModal, setDeleteModal] = useState(null);
    const [preview, setPreview] = useState(null);
    const [togglingIds, setTogglingIds] = useState([]);

    const showFeedback = useCallback((type, message) => {
        clearTimeout(feedbackTimer.current);
        setFeedback({ type, message });
        if (type === 'success') {
            feedbackTimer.current = setTimeout(() => setFeedback(null), 5000);
        }
    }, []);

    useEffect(() => () => clearTimeout(feedbackTimer.current), []);

    const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

    const fetchNotifications = useCallback(async () => {
        try {
            const response = await axios.get('/api/notifications/html-content', { headers: authHeaders() });
            setNotifications((response.data || []).map((notification) => ({
                ...notification,
                language: normalizeLanguageValue(notification.language)
            })));
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
            setNotifications([]);
            showFeedback('error', t('notificationsPage.fetchError', 'Impossible de charger les modèles de notifications.'));
        }
    }, [showFeedback, t]);

    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            navigate('/');
            return;
        }
        const headers = { Authorization: `Bearer ${token}` };
        axios.get('/api/users/user', { headers })
            .then((res) => {
                setUser(res.data);
                if (res.data?.has_brand) {
                    axios.get('/api/brands/list', { headers })
                        .then((r) => setBrands(r.data || []))
                        .catch((err) => console.error('Failed to fetch brands:', err));
                }
            })
            .catch((error) => {
                console.error('Failed to fetch user data:', error);
                navigate('/');
            });
        axios.get('/api/notifications/notification-types', { headers })
            .then((res) => setNotificationTypes(res.data || []))
            .catch((err) => console.error('Failed to fetch notification types:', err));
        fetchNotifications();
    }, [navigate, fetchNotifications]);

    const canManage = user && ['admin', 'seller', 'eshop'].includes(user.role);

    // ------------------------------------------------------------ metadata

    const TYPE_META = {
        ticket_created: {
            label: t('notificationsPage.typeTicketCreated', 'Ticket créé'),
            hint: t('notificationsPage.typeTicketCreatedHint', 'envoyé au client quand un ticket est créé depuis le site ou un formulaire'),
        },
        ticket_created_by_mail: {
            label: t('notificationsPage.typeTicketCreatedByMail', 'Ticket créé par email'),
            hint: t('notificationsPage.typeTicketCreatedByMailHint', 'accusé de réception envoyé quand un email entrant ouvre un ticket'),
        },
        response_received: {
            label: t('notificationsPage.typeResponseReceived', 'Réponse reçue'),
            hint: t('notificationsPage.typeResponseReceivedHint', 'envoyé au client quand une réponse est publiée sur son ticket'),
        },
        outlook_reply: {
            label: t('notificationsPage.typeOutlookReply', 'Réponse Outlook'),
            hint: t('notificationsPage.typeOutlookReplyHint', 'habillage des réponses envoyées via une boîte Outlook connectée'),
        },
        client_email: {
            label: t('notificationsPage.typeClientEmail', 'Email client'),
            hint: t('notificationsPage.typeClientEmailHint', 'habillage des emails envoyés directement au client'),
        },
    };

    const typeLabel = (type) => TYPE_META[type]?.label || type;

    const getBrandName = (brandId) => {
        if (!brandId) return t('notificationsPage.userLevel', 'Niveau utilisateur');
        const brand = brands.find((b) => b.id === brandId);
        return brand ? brand.name : brandId;
    };

    const formatDate = (dateString) => {
        if (!dateString) return null;
        return new Date(dateString).toLocaleDateString(i18n.language || 'fr', {
            day: 'numeric', month: 'short', year: 'numeric'
        });
    };

    const languageOptions = (() => {
        const langs = new Set([DEFAULT_LANGUAGE, ...(notifications || []).map((n) => n.language)]);
        return [...langs].sort();
    })();

    // Merge the backend-declared types with any type present in the data so
    // nothing is ever silently dropped.
    const allTypes = (() => {
        const known = new Set([...TYPE_ORDER, ...notificationTypes, ...(notifications || []).map((n) => n.notification_type)]);
        return [...TYPE_ORDER.filter((tp) => known.has(tp)), ...[...known].filter((tp) => !TYPE_ORDER.includes(tp)).sort()];
    })();

    // ------------------------------------------------------------ filtering

    const q = searchTerm.toLowerCase();
    const matchesFilters = (notification) => {
        if (languageFilter && notification.language !== languageFilter) return false;
        if (brandFilter === 'user-level' && notification.brand_id) return false;
        if (brandFilter && brandFilter !== 'user-level' && notification.brand_id !== brandFilter) return false;
        if (q) {
            const hay = `${notification.name} ${notification.subject} ${notification.description || ''}`.toLowerCase();
            if (!hay.includes(q)) return false;
        }
        return true;
    };

    const hasActiveFilters = !!(searchTerm || languageFilter || brandFilter);

    // ------------------------------------------------------------ actions

    const openCreateModal = (presetType = '') => {
        if (!canManage) return;
        setModal({ editing: null, form: { ...emptyForm(), notification_type: presetType }, error: null, saving: false, view: 'code' });
    };

    const openEditModal = (notification) => {
        if (!canManage) return;
        setModal({
            editing: notification,
            form: {
                name: notification.name,
                notification_type: notification.notification_type,
                subject: notification.subject,
                html_content: notification.html_content,
                description: notification.description || '',
                is_active: notification.is_active,
                language: notification.language,
                brand_id: notification.brand_id || ''
            },
            error: null,
            saving: false,
            view: 'code',
        });
    };

    const setForm = (patch) => setModal((m) => ({ ...m, form: { ...m.form, ...patch }, error: null }));

    const handleSubmit = async () => {
        const { form, editing } = modal;
        if (!form.name.trim() || !form.notification_type || !form.subject.trim() || !form.html_content.trim()) {
            setModal((m) => ({ ...m, error: t('notificationsPage.requiredFields', 'Nom, événement, objet et contenu HTML sont requis.') }));
            return;
        }
        setModal((m) => ({ ...m, saving: true, error: null }));
        const payload = { ...form, language: normalizeLanguageValue(form.language) };
        try {
            if (editing) {
                await axios.put(`/api/notifications/html-content/${editing.id}`, payload, { headers: authHeaders() });
                showFeedback('success', t('notificationsPage.updated', 'Modèle mis à jour.'));
            } else {
                await axios.post('/api/notifications/html-content', payload, { headers: authHeaders() });
                showFeedback('success', t('notificationsPage.created', 'Modèle créé.'));
            }
            setModal(null);
            fetchNotifications();
        } catch (error) {
            console.error('Failed to save notification:', error);
            setModal((m) => ({
                ...m, saving: false,
                error: error.response?.data?.detail || t('notificationsPage.saveError', 'Échec de l’enregistrement du modèle.'),
            }));
        }
    };

    const confirmDelete = async () => {
        setDeleteModal((m) => ({ ...m, deleting: true, error: null }));
        try {
            await axios.delete(`/api/notifications/html-content/${deleteModal.notification.id}`, { headers: authHeaders() });
            setDeleteModal(null);
            showFeedback('success', t('notificationsPage.deleted', 'Modèle supprimé.'));
            fetchNotifications();
        } catch (error) {
            console.error('Failed to delete notification:', error);
            setDeleteModal((m) => ({
                ...m, deleting: false,
                error: error.response?.data?.detail || t('notificationsPage.deleteError', 'Échec de la suppression du modèle.'),
            }));
        }
    };

    const toggleActiveStatus = async (notification) => {
        if (!canManage) return;
        setTogglingIds((prev) => [...prev, notification.id]);
        try {
            await axios.put(`/api/notifications/html-content/${notification.id}`,
                { is_active: !notification.is_active },
                { headers: authHeaders() }
            );
            await fetchNotifications();
        } catch (error) {
            console.error('Failed to toggle notification status:', error);
            showFeedback('error', t('notificationsPage.toggleError', 'Échec de l’activation/désactivation du modèle.'));
        } finally {
            setTogglingIds((prev) => prev.filter((id) => id !== notification.id));
        }
    };

    // ------------------------------------------------------------ render

    const loading = notifications === null;
    const list = notifications || [];

    return (
        <div className="notifications-page">
            {/* Header */}
            <div className="nt-header">
                <div className="nt-heading">
                    <h1>{t('notificationsPage.title', 'Modèles de notifications')}</h1>
                    <p>{t('notificationsPage.subtitle', 'Emails envoyés automatiquement à chaque événement. Seuls les modèles actifs sont utilisés — marque + langue d’abord, sinon repli EN, sinon niveau utilisateur.')}</p>
                </div>
                {canManage && (
                    <button type="button" className="u-btn u-btn--primary" onClick={() => openCreateModal()}>
                        <FaPlus /> {t('notificationsPage.create', 'Créer un modèle')}
                    </button>
                )}
            </div>

            {/* Toolbar */}
            <div className="nt-toolbar">
                <div className="nt-search">
                    <FaSearch aria-hidden="true" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => updateParams({ q: e.target.value })}
                        placeholder={t('notificationsPage.searchPlaceholder', 'Rechercher un modèle, un objet…')}
                    />
                    {searchTerm && (
                        <button type="button" className="nt-search-clear" onClick={() => updateParams({ q: null })} aria-label={t('common.cancel', 'Effacer')}>
                            <FaTimes />
                        </button>
                    )}
                </div>
                <label className="nt-toolbar-field">
                    <span>{t('notificationsPage.language', 'Langue')}</span>
                    <select value={languageFilter} onChange={(e) => updateParams({ lang: e.target.value })}>
                        <option value="">{t('notificationsPage.allLanguages', 'Toutes')}</option>
                        {languageOptions.map((language) => (
                            <option key={language} value={language}>{language}</option>
                        ))}
                    </select>
                </label>
                {user?.has_brand && brands.length > 0 && (
                    <label className="nt-toolbar-field">
                        <span>{t('notificationsPage.brand', 'Marque')}</span>
                        <select value={brandFilter} onChange={(e) => updateParams({ brand: e.target.value })}>
                            <option value="">{t('notificationsPage.allBrands', 'Toutes')}</option>
                            <option value="user-level">{t('notificationsPage.userLevel', 'Niveau utilisateur')}</option>
                            {brands.map((brand) => (
                                <option key={brand.id} value={brand.id}>{brand.name}</option>
                            ))}
                        </select>
                    </label>
                )}
            </div>

            {feedback && (
                <div className={`nt-feedback ${feedback.type}`} role="status">
                    {feedback.type === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
                    {feedback.message}
                </div>
            )}

            {loading ? (
                <div className="nt-skeleton" aria-hidden="true">
                    {Array.from({ length: 4 }, (_, i) => <div key={i} className="nt-skeleton-row" />)}
                </div>
            ) : (
                <div className="nt-stack">
                    {allTypes.map((type) => {
                        const templates = list.filter((n) => n.notification_type === type);
                        const visible = templates.filter(matchesFilters);
                        const activeCount = templates.filter((n) => n.is_active).length;
                        return (
                            <div key={type} className="nt-card">
                                <div className="nt-card-header">
                                    <div className="nt-card-heading">
                                        <h2>
                                            {typeLabel(type)}
                                            {templates.length > 0 && (
                                                <span className="nt-card-count">
                                                    {t('notificationsPage.typeCount', '{{active}} actifs / {{total}}', { active: activeCount, total: templates.length })}
                                                </span>
                                            )}
                                        </h2>
                                        {TYPE_META[type]?.hint && <p>{TYPE_META[type].hint}</p>}
                                    </div>
                                    {canManage && (
                                        <button type="button" className="nt-add-btn" onClick={() => openCreateModal(type)}>
                                            <FaPlus /> {t('notificationsPage.addTemplate', 'Ajouter')}
                                        </button>
                                    )}
                                </div>

                                {templates.length === 0 ? (
                                    <p className="nt-card-empty">
                                        {t('notificationsPage.noTemplatesForType', 'Aucun modèle personnalisé — l’email par défaut de la plateforme est utilisé.')}
                                    </p>
                                ) : visible.length === 0 ? (
                                    <p className="nt-card-empty">
                                        {hasActiveFilters
                                            ? t('notificationsPage.noMatchesForType', 'Aucun modèle ne correspond aux filtres pour cet événement.')
                                            : null}
                                    </p>
                                ) : (
                                    <ul className="nt-list">
                                        {visible.map((notification) => {
                                            const meta = [
                                                formatDate(notification.created_at) && t('notificationsPage.metaCreated', 'Créé le {{date}}', { date: formatDate(notification.created_at) }),
                                                notification.updated_at !== notification.created_at && formatDate(notification.updated_at) && t('notificationsPage.metaUpdated', 'modifié le {{date}}', { date: formatDate(notification.updated_at) }),
                                                notification.created_by && t('notificationsPage.metaBy', 'par {{author}}', { author: notification.created_by }),
                                            ].filter(Boolean).join(' · ');
                                            return (
                                                <li key={notification.id} className={`nt-row ${!notification.is_active ? 'inactive' : ''}`}>
                                                    <div className="nt-row-main" title={meta}>
                                                        <div className="nt-row-title">
                                                            <span className="nt-row-name">{notification.name}</span>
                                                            <span className="nt-chip">{notification.language}</span>
                                                            {user?.has_brand && (
                                                                <span className={`nt-chip ${notification.brand_id ? 'brand' : ''}`}>
                                                                    {getBrandName(notification.brand_id)}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <span className="nt-row-subject" title={notification.subject}>{notification.subject}</span>
                                                    </div>
                                                    <div className="nt-row-actions">
                                                        {canManage && (
                                                            <button
                                                                type="button"
                                                                role="switch"
                                                                aria-checked={notification.is_active}
                                                                className={`nt-switch ${notification.is_active ? 'on' : ''}`}
                                                                disabled={togglingIds.includes(notification.id)}
                                                                onClick={() => toggleActiveStatus(notification)}
                                                                title={notification.is_active
                                                                    ? t('notificationsPage.deactivate', 'Désactiver — ce modèle ne sera plus utilisé')
                                                                    : t('notificationsPage.activate', 'Activer')}
                                                            >
                                                                <span className="nt-switch-knob" />
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            className="nt-icon-btn"
                                                            onClick={() => setPreview(notification)}
                                                            title={t('notificationsPage.preview', 'Aperçu')}
                                                            aria-label={t('notificationsPage.preview', 'Aperçu')}
                                                        >
                                                            <FaEye />
                                                        </button>
                                                        {canManage && (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    className="nt-icon-btn"
                                                                    onClick={() => openEditModal(notification)}
                                                                    title={t('notificationsPage.edit', 'Modifier')}
                                                                    aria-label={t('notificationsPage.edit', 'Modifier')}
                                                                >
                                                                    <FaPen />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="nt-icon-btn danger"
                                                                    onClick={() => setDeleteModal({ notification, deleting: false, error: null })}
                                                                    title={t('notificationsPage.delete', 'Supprimer')}
                                                                    aria-label={t('notificationsPage.delete', 'Supprimer')}
                                                                >
                                                                    <FaTrash />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Unified create/edit modal */}
            {modal && (
                <div className="nt-modal-backdrop" onClick={() => setModal(null)}>
                    <div className="nt-modal nt-modal-wide" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                        <div className="nt-modal-header">
                            <h3>
                                {modal.editing
                                    ? t('notificationsPage.editTitle', 'Modifier le modèle')
                                    : t('notificationsPage.createTitle', 'Créer un modèle')}
                            </h3>
                            <button type="button" className="nt-icon-btn" onClick={() => setModal(null)} aria-label={t('common.cancel', 'Annuler')}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="nt-modal-body">
                            <div className="nt-form-section">
                                <h4>{t('notificationsPage.sectionEvent', 'Événement')}</h4>
                                <div className="nt-form-grid">
                                    <div className="nt-field">
                                        <label htmlFor="nt-name">{t('notificationsPage.templateName', 'Nom du modèle')} *</label>
                                        <input
                                            id="nt-name"
                                            type="text"
                                            maxLength={100}
                                            value={modal.form.name}
                                            onChange={(e) => setForm({ name: e.target.value })}
                                            autoFocus
                                        />
                                    </div>
                                    <div className="nt-field">
                                        <label htmlFor="nt-type">{t('notificationsPage.eventType', 'Type d’événement')} *</label>
                                        <select
                                            id="nt-type"
                                            value={modal.form.notification_type}
                                            onChange={(e) => setForm({ notification_type: e.target.value })}
                                        >
                                            <option value="">{t('notificationsPage.selectType', 'Sélectionner…')}</option>
                                            {allTypes.map((type) => (
                                                <option key={type} value={type}>{typeLabel(type)}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                {modal.form.notification_type && TYPE_META[modal.form.notification_type]?.hint && (
                                    <p className="nt-field-hint">{TYPE_META[modal.form.notification_type].hint}</p>
                                )}
                            </div>

                            <div className="nt-form-section">
                                <h4>{t('notificationsPage.sectionScope', 'Portée')}</h4>
                                <div className="nt-form-grid">
                                    <div className="nt-field">
                                        <label htmlFor="nt-lang">{t('notificationsPage.language', 'Langue')} *</label>
                                        <input
                                            id="nt-lang"
                                            type="text"
                                            maxLength={10}
                                            value={modal.form.language}
                                            onChange={(e) => setForm({ language: e.target.value.toUpperCase() })}
                                            placeholder="FR"
                                            list="nt-language-options"
                                        />
                                        <datalist id="nt-language-options">
                                            {languageOptions.map((language) => (
                                                <option key={language} value={language} />
                                            ))}
                                        </datalist>
                                    </div>
                                    {user?.has_brand && brands.length > 0 && (
                                        <div className="nt-field">
                                            <label htmlFor="nt-brand">{t('notificationsPage.brand', 'Marque')}</label>
                                            <select
                                                id="nt-brand"
                                                value={modal.form.brand_id}
                                                onChange={(e) => setForm({ brand_id: e.target.value })}
                                            >
                                                <option value="">{t('notificationsPage.userLevel', 'Niveau utilisateur')}</option>
                                                {brands.map((brand) => (
                                                    <option key={brand.id} value={brand.id}>{brand.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                                <p className="nt-field-hint">
                                    {t('notificationsPage.scopeHint', 'Un modèle de marque prime sur le niveau utilisateur ; la langue EN sert de repli.')}
                                </p>
                            </div>

                            <div className="nt-form-section">
                                <h4>{t('notificationsPage.sectionContent', 'Contenu')}</h4>
                                <div className="nt-field">
                                    <label htmlFor="nt-subject">{t('notificationsPage.emailSubject', 'Objet de l’email')} *</label>
                                    <input
                                        id="nt-subject"
                                        type="text"
                                        maxLength={200}
                                        value={modal.form.subject}
                                        onChange={(e) => setForm({ subject: e.target.value })}
                                    />
                                </div>
                                <div className="nt-field">
                                    <label htmlFor="nt-desc">{t('notificationsPage.description', 'Description')}</label>
                                    <input
                                        id="nt-desc"
                                        type="text"
                                        maxLength={500}
                                        value={modal.form.description}
                                        onChange={(e) => setForm({ description: e.target.value })}
                                        placeholder={t('notificationsPage.descriptionPlaceholder', 'Optionnelle — note interne')}
                                    />
                                </div>
                                <div className="nt-field">
                                    <div className="nt-html-head">
                                        <label htmlFor="nt-html">{t('notificationsPage.htmlContent', 'Contenu HTML')} *</label>
                                        <div className="nt-view-tabs" role="tablist">
                                            <button
                                                type="button"
                                                role="tab"
                                                aria-selected={modal.view === 'code'}
                                                className={modal.view === 'code' ? 'active' : ''}
                                                onClick={() => setModal((m) => ({ ...m, view: 'code' }))}
                                            >
                                                {t('notificationsPage.viewCode', 'Code')}
                                            </button>
                                            <button
                                                type="button"
                                                role="tab"
                                                aria-selected={modal.view === 'preview'}
                                                className={modal.view === 'preview' ? 'active' : ''}
                                                onClick={() => setModal((m) => ({ ...m, view: 'preview' }))}
                                            >
                                                {t('notificationsPage.viewPreview', 'Aperçu')}
                                            </button>
                                        </div>
                                    </div>
                                    {modal.view === 'code' ? (
                                        <textarea
                                            id="nt-html"
                                            className="nt-code-textarea"
                                            rows={12}
                                            value={modal.form.html_content}
                                            onChange={(e) => setForm({ html_content: e.target.value })}
                                            placeholder={t('notificationsPage.htmlPlaceholder', 'Contenu HTML de l’email…')}
                                            spellCheck={false}
                                        />
                                    ) : (
                                        <div className="nt-live-preview">
                                            {modal.form.html_content.trim() ? (
                                                <iframe
                                                    srcDoc={modal.form.html_content}
                                                    title={t('notificationsPage.viewPreview', 'Aperçu')}
                                                    sandbox=""
                                                />
                                            ) : (
                                                <p className="nt-preview-empty">{t('notificationsPage.previewEmpty', 'Aucun contenu HTML à prévisualiser.')}</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="nt-active-row">
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={modal.form.is_active}
                                        className={`nt-switch ${modal.form.is_active ? 'on' : ''}`}
                                        onClick={() => setForm({ is_active: !modal.form.is_active })}
                                    >
                                        <span className="nt-switch-knob" />
                                    </button>
                                    <span>
                                        {modal.form.is_active
                                            ? t('notificationsPage.activeLabel', 'Actif — utilisable dès l’enregistrement')
                                            : t('notificationsPage.inactiveLabel', 'Inactif — jamais sélectionné à l’envoi')}
                                    </span>
                                </div>
                            </div>

                            {modal.error && <div className="nt-modal-error">{modal.error}</div>}
                        </div>
                        <div className="nt-modal-footer">
                            <button type="button" className="u-btn u-btn--secondary" onClick={() => setModal(null)} disabled={modal.saving}>
                                {t('common.cancel', 'Annuler')}
                            </button>
                            <button type="button" className="u-btn u-btn--primary" onClick={handleSubmit} disabled={modal.saving}>
                                {modal.saving
                                    ? (<><FaSpinner className="nt-spin" /> {t('common.saving', 'Enregistrement…')}</>)
                                    : modal.editing
                                        ? t('common.save', 'Enregistrer')
                                        : t('notificationsPage.submitCreate', 'Créer le modèle')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete confirmation */}
            {deleteModal && (
                <div className="nt-modal-backdrop" onClick={() => setDeleteModal(null)}>
                    <div className="nt-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                        <div className="nt-modal-header">
                            <h3><FaExclamationTriangle className="nt-danger-icon" /> {t('notificationsPage.deleteTitle', 'Supprimer le modèle')}</h3>
                            <button type="button" className="nt-icon-btn" onClick={() => setDeleteModal(null)} aria-label={t('common.cancel', 'Annuler')}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="nt-modal-body">
                            <p className="nt-delete-text">
                                {t('notificationsPage.deleteConfirm', 'Supprimer « {{name}} » ({{type}}) ? Cette action est irréversible.', {
                                    name: deleteModal.notification.name,
                                    type: typeLabel(deleteModal.notification.notification_type),
                                })}
                            </p>
                            {deleteModal.error && <div className="nt-modal-error">{deleteModal.error}</div>}
                        </div>
                        <div className="nt-modal-footer">
                            <button type="button" className="u-btn u-btn--secondary" onClick={() => setDeleteModal(null)} disabled={deleteModal.deleting}>
                                {t('common.cancel', 'Annuler')}
                            </button>
                            <button type="button" className="nt-danger-btn" onClick={confirmDelete} disabled={deleteModal.deleting}>
                                {deleteModal.deleting
                                    ? (<><FaSpinner className="nt-spin" /> {t('notificationsPage.deleting', 'Suppression…')}</>)
                                    : (<><FaTrash /> {t('notificationsPage.delete', 'Supprimer')}</>)}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview modal */}
            {preview && (
                <div className="nt-modal-backdrop" onClick={() => setPreview(null)}>
                    <div className="nt-modal nt-preview-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                        <div className="nt-modal-header">
                            <h3 title={preview.name}><FaBell className="nt-preview-icon" /> {preview.name}</h3>
                            <button type="button" className="nt-icon-btn" onClick={() => setPreview(null)} aria-label={t('common.cancel', 'Fermer')}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="nt-preview-meta">
                            <span><b>{t('notificationsPage.emailSubject', 'Objet de l’email')} :</b> {preview.subject}</span>
                            <span className="nt-chip">{preview.language}</span>
                            {user?.has_brand && (
                                <span className={`nt-chip ${preview.brand_id ? 'brand' : ''}`}>{getBrandName(preview.brand_id)}</span>
                            )}
                        </div>
                        <div className="nt-preview-body">
                            <iframe srcDoc={preview.html_content} title={preview.name} sandbox="" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationsSettings;
